import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import FilterChip from './FilterChip';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { ROLE_TYPES, getRoleChipStyle } from '../../../constants/roleTypes';

/**
 * ADR-029 (Workforce Composition Reporting) — axis-parameterised org-role
 * filter, one instance per axis (Functional / Technical / Organizational).
 *
 * Deliberately built on the Grades.jsx chip pattern and NOT on the dead
 * RolesSelector.jsx, which reads `role.name` (the column is `label`) and
 * collapses the three role types into two. Colours come from
 * getRoleChipStyle — the only three-way-correct helper in roleTypes.js.
 *
 * Props:
 * - axis: one of ROLE_TYPES.* — which axis this instance filters
 * - selectedRoleIds / onRoleToggle: controlled selection (ids)
 * - roles: the org-role catalog, three-valued:
 *     undefined — the component fetches the catalog itself (getAllRoles)
 *     null      — the PARENT will supply it; render loading, do NOT fetch
 *     array     — render from it (e.g. `structure.roles` from a single
 *                 GET /api/organization/structure call shared by several
 *                 filter components, ADR-029 D9)
 * - availableRoleIds: optional narrowing set (e.g. the selected position's
 *   attached roles). Roles outside it render disabled, mirroring the
 *   admin screen's dimmed-until-position-selected behaviour.
 */
const OrgRoleFilter = ({
  axis,
  selectedRoleIds = [],
  onRoleToggle,
  roles,
  availableRoleIds = null,
  sx = {}
}) => {
  const selfFetch = roles === undefined;
  const [fetchedRoles, setFetchedRoles] = useState([]);
  const [selfFetchLoading, setSelfFetchLoading] = useState(selfFetch);
  const { showAlert } = useAlert();

  useEffect(() => {
    if (!selfFetch) return undefined;
    let cancelled = false;
    const fetchRoles = async () => {
      try {
        setSelfFetchLoading(true);
        const rolesData = await organizationApi.getAllRoles();
        if (!cancelled) setFetchedRoles(rolesData || []);
      } catch (error) {
        if (!cancelled) showAlert('Failed to load roles', 'error', error);
      } finally {
        if (!cancelled) setSelfFetchLoading(false);
      }
    };
    fetchRoles();
    return () => { cancelled = true; };
  }, [selfFetch, showAlert]);

  const { bgColor, textColor } = getRoleChipStyle(axis);

  // loading = self-fetch in flight, OR parent has promised roles (null) but
  // hasn't delivered yet. An array prop always renders immediately — the
  // parent supplying the catalog late must never strand us in loading.
  const loading = selfFetch ? selfFetchLoading : roles === null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ...sx }}>
        <FilterChip
          label="Loading roles..."
          isSelected={false}
          onClick={() => {}}
          backgroundColor="#f3f4f6"
          textColor="#6b7280"
          borderColor="#e5e7eb"
          disabled
        />
      </Box>
    );
  }

  const axisRoles = (selfFetch ? fetchedRoles : (roles || []))
    .filter(role => role.type === axis)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ...sx }}>
      {axisRoles.map((role) => {
        const isSelected = selectedRoleIds.includes(role.id);
        const isAvailable = availableRoleIds === null || availableRoleIds.includes(role.id);
        // A SELECTED chip must always stay clickable, even when narrowing
        // makes it unavailable — otherwise a stale selection has no way to
        // be removed and keeps constraining the query.
        const clickable = isAvailable || isSelected;

        return (
          <FilterChip
            key={role.id}
            label={role.label}
            isSelected={isSelected}
            onClick={() => clickable && onRoleToggle(role.id)}
            backgroundColor={isSelected ? textColor : bgColor}
            textColor={isSelected ? 'white' : textColor}
            borderColor={textColor}
            showDot={true}
            sx={clickable ? {} : { opacity: 0.4, pointerEvents: 'none' }}
          />
        );
      })}
    </Box>
  );
};

export default OrgRoleFilter;
